import { fetchWithRetry } from './api';

const API_BASE = 'https://api.blockchain.info';
const CACHE_KEY_PREFIX = 'blockchain_info_';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export interface BlockchainDataPoint {
  x: number; // Unix timestamp (seconds)
  y: number; // Value
}

export interface BlockchainChartData {
  status: string;
  name: string;
  unit: string;
  period: string;
  description: string;
  values: BlockchainDataPoint[];
}

interface CacheData {
  data: BlockchainChartData;
  lastUpdated: number;
}

/**
 * Blockchain.info API Service
 * Provides hashrate, difficulty, and other Bitcoin network metrics
 * Free API with no rate limits
 */
export const BlockchainInfoAPI = {
  /**
   * Get cached data
   */
  getCache(chartType: string): BlockchainChartData | null {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${chartType}`;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const data: CacheData = JSON.parse(cached);

      // Check if cache is still valid
      if (Date.now() - data.lastUpdated > CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data.data;
    } catch (error) {
      console.error(`Error reading cache for ${chartType}:`, error);
      return null;
    }
  },

  /**
   * Save data to cache
   */
  saveCache(chartType: string, data: BlockchainChartData): void {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${chartType}`;
      const cacheData: CacheData = {
        data,
        lastUpdated: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error(`Error saving cache for ${chartType}:`, error);
    }
  },

  /**
   * Fetch hashrate data
   * Unit: TH/s (terahashes per second)
   * @param timespan - Optional timespan (default: 'all')
   */
  async getHashrate(timespan: string = 'all'): Promise<BlockchainChartData> {
    // Check cache first
    const cached = this.getCache(`hashrate_${timespan}`);
    if (cached) {
      console.log(`✅ Hashrate data (${timespan}) found in cache`);
      return cached;
    }

    // Fetch from API
    try {
      console.log(`📡 Fetching hashrate data from Blockchain.info (${timespan})...`);

      const url = `${API_BASE}/charts/hash-rate?timespan=${timespan}&format=json`;
      const response = await fetchWithRetry(url, {}, 3, true);
      const data: BlockchainChartData = await response.json();

      // Save to cache
      this.saveCache(`hashrate_${timespan}`, data);

      console.log(`✅ Loaded ${data.values.length} hashrate data points`);
      console.log(`📊 Hashrate info: name="${data.name}", unit="${data.unit}", period="${data.period}"`);

      if (data.values.length > 0) {
        const lastValue = data.values[data.values.length - 1];
        console.log(`📊 Latest hashrate: ${lastValue.y.toLocaleString()} ${data.unit} (date: ${new Date(lastValue.x * 1000).toISOString().split('T')[0]})`);
      }
      return data;
    } catch (error) {
      console.error('❌ Error fetching hashrate data:', error);
      throw error;
    }
  },

  /**
   * Fetch difficulty data
   * @param timespan - Optional timespan (default: 'all')
   */
  async getDifficulty(timespan: string = 'all'): Promise<BlockchainChartData> {
    // Check cache first
    const cached = this.getCache(`difficulty_${timespan}`);
    if (cached) {
      console.log(`✅ Difficulty data (${timespan}) found in cache`);
      return cached;
    }

    // Fetch from API
    try {
      console.log(`📡 Fetching difficulty data from Blockchain.info (${timespan})...`);

      const url = `${API_BASE}/charts/difficulty?timespan=${timespan}&format=json`;
      const response = await fetchWithRetry(url, {}, 3, true);
      const data: BlockchainChartData = await response.json();

      // Save to cache
      this.saveCache(`difficulty_${timespan}`, data);

      console.log(`✅ Loaded ${data.values.length} difficulty data points`);
      console.log(`📊 Difficulty info: name="${data.name}", unit="${data.unit}", period="${data.period}"`);

      if (data.values.length > 0) {
        const lastValue = data.values[data.values.length - 1];
        console.log(`📊 Latest difficulty: ${lastValue.y.toLocaleString()} ${data.unit} (date: ${new Date(lastValue.x * 1000).toISOString().split('T')[0]})`);
      }
      return data;
    } catch (error) {
      console.error('❌ Error fetching difficulty data:', error);
      throw error;
    }
  },

  /**
   * Fetch both hashrate and difficulty in parallel
   * @param timespan - Optional timespan (default: 'all')
   */
  async getHashrateAndDifficulty(timespan: string = 'all'): Promise<{
    hashrate: BlockchainChartData;
    difficulty: BlockchainChartData;
  }> {
    try {
      const [hashrate, difficulty] = await Promise.all([
        this.getHashrate(timespan),
        this.getDifficulty(timespan)
      ]);

      return { hashrate, difficulty };
    } catch (error) {
      console.error('❌ Error fetching hashrate and difficulty:', error);
      throw error;
    }
  },

  /**
   * Convert hashrate to TH/s based on the unit returned by the API
   */
  convertHashrateToTHs(value: number, unit: string): number {
    const unitLower = unit.toLowerCase();

    // Check for common hashrate units
    if (unitLower.includes('th/s') || unitLower.includes('terahash')) {
      return value; // Already in TH/s
    } else if (unitLower.includes('ph/s') || unitLower.includes('petahash')) {
      return value * 1000; // PH/s to TH/s
    } else if (unitLower.includes('eh/s') || unitLower.includes('exahash')) {
      return value * 1000000; // EH/s to TH/s
    } else if (unitLower.includes('gh/s') || unitLower.includes('gigahash')) {
      return value / 1000; // GH/s to TH/s
    } else {
      // Unknown unit, assume TH/s and log warning
      console.warn(`⚠️ Unknown hashrate unit "${unit}", assuming TH/s`);
      return value;
    }
  },

  /**
   * Get hashrate value for a specific timestamp
   * Uses linear interpolation if exact timestamp not found
   * Returns value in TH/s
   */
  getHashrateAt(data: BlockchainChartData, timestamp: number): number | null {
    const timestampSeconds = Math.floor(timestamp / 1000);

    // Find closest points
    const values = data.values;

    // Exact match
    const exact = values.find(v => v.x === timestampSeconds);
    if (exact) {
      const value = this.convertHashrateToTHs(exact.y, data.unit);
      return value;
    }

    // Find surrounding points for interpolation
    let before: BlockchainDataPoint | null = null;
    let after: BlockchainDataPoint | null = null;

    for (let i = 0; i < values.length; i++) {
      if (values[i].x < timestampSeconds) {
        before = values[i];
      } else if (values[i].x > timestampSeconds) {
        after = values[i];
        break;
      }
    }

    // If no surrounding points, return null
    if (!before && !after) return null;
    if (!before) return this.convertHashrateToTHs(after!.y, data.unit);
    if (!after) return this.convertHashrateToTHs(before.y, data.unit);

    // Linear interpolation
    const ratio = (timestampSeconds - before.x) / (after.x - before.x);
    const interpolatedValue = before.y + (after.y - before.y) * ratio;
    return this.convertHashrateToTHs(interpolatedValue, data.unit);
  },

  /**
   * Get difficulty value for a specific timestamp
   * Uses linear interpolation if exact timestamp not found
   */
  getDifficultyAt(data: BlockchainChartData, timestamp: number): number | null {
    const timestampSeconds = Math.floor(timestamp / 1000);

    // Find closest points
    const values = data.values;

    // Exact match
    const exact = values.find(v => v.x === timestampSeconds);
    if (exact) return exact.y;

    // Find surrounding points for interpolation
    let before: BlockchainDataPoint | null = null;
    let after: BlockchainDataPoint | null = null;

    for (let i = 0; i < values.length; i++) {
      if (values[i].x < timestampSeconds) {
        before = values[i];
      } else if (values[i].x > timestampSeconds) {
        after = values[i];
        break;
      }
    }

    // If no surrounding points, return null
    if (!before && !after) return null;
    if (!before) return after!.y;
    if (!after) return before.y;

    // Linear interpolation
    const ratio = (timestampSeconds - before.x) / (after.x - before.x);
    return before.y + (after.y - before.y) * ratio;
  },

  /**
   * Clear all caches
   */
  clearCache(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      console.log('🗑️ Blockchain.info cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
};
